import axios from 'axios';
import { useEffect, useRef } from 'react';
import { TOKEN_ENDPOINT, CLIENT_ID } from '../../app_constants';

import getAuthUrl from '../getAuthUrl';

type TokenMessage =
  | 'get_expiration_time'
  | 'expiration_time_response'
  | 'request_refresh_token'
  | 'token_refreshed'
  | 'token_refresh_failed'
  | 'start'
  | 'stop';

const authAxios = axios.create();

export const removeToken = (): void => {
  // Если сервер вернул ошибку при обновлении
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('tokenExpirationTime');
  localStorage.removeItem('id_token');
  localStorage.removeItem('tokenLifetime');
  localStorage.removeItem('code_verifier');
};

export const redirectToSsoLogin = (): void => {
  removeToken();
  getAuthUrl()
    .then((url) => {
      if (url) {
        window.location.href = url;
      } else {
        // Fallback на случай, если сервис вернул ошибку или пустой URL
        console.error('SSO URL не был получен от сервера.');
        window.location.href = '/';
      }
    })
    .catch((error) => {
      console.error('Ошибка при получении SSO URL:', error);
      window.location.href = '/';
    });
};

export const refreshToken = async () => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  if (!refreshTokenValue) {
    throw new Error('No refresh token');
  }

  const formData = new URLSearchParams();
  formData.append('refresh_token', refreshTokenValue);
  formData.append('grant_type', 'refresh_token');
  formData.append('client_id', CLIENT_ID);

  const response = await authAxios.post(TOKEN_ENDPOINT, formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });

  if (response.status === 200) {
    // Добавляем буфер в 3 секунды к времени жизни токена
    const expiresInSeconds = Number(localStorage.getItem('tokenLifetime'));
    const newTokenExpirationTime = Date.now() + (expiresInSeconds - 3) * 1000;
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);
    localStorage.setItem('tokenExpirationTime', String(newTokenExpirationTime));
    localStorage.setItem('id_token', response.data.id_token);
    return response.data.access_token;
  }
  removeToken();

  throw new Error('Token refresh failed');
};

export const useTokenRefreshService = () => {
  const workerRef = useRef<Worker | null>(null);

  const handleWorkerMessage = (event: MessageEvent) => {
    const messageAction = (event.data as { action: TokenMessage }).action;
    switch (messageAction) {
      case 'get_expiration_time':
        console.log(
          'Хук: Посылаем воркеру время жизни токена',
          localStorage.getItem('tokenExpirationTime'),
        );
        workerRef.current?.postMessage({
          action: 'expiration_time_response',
          value: localStorage.getItem('tokenExpirationTime'),
        });
        break;

      case 'request_refresh_token':
        refreshToken()
          .then(() => {
            workerRef.current?.postMessage({ action: 'token_refreshed' });
          })
          .catch((err) => {
            console.error('Ошибка при обновлении токена в хуке:', err);
            workerRef.current?.postMessage({ action: 'token_refresh_failed' });
          });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const cleanup = () => {
      if (workerRef.current) {
        workerRef.current.postMessage({ action: 'stop' });
        setTimeout(() => {
          workerRef.current?.removeEventListener('message', handleWorkerMessage);
          workerRef.current?.terminate(); // Обязательно завершаем воркер!
        }, 0);
      }
    };
    if (!workerRef.current) {
      try {
        workerRef.current = new Worker(new URL('../../workers/tokenRefresh.ts', import.meta.url));
        workerRef.current?.addEventListener('message', handleWorkerMessage);
        const hasToken = !!localStorage.getItem('access_token');
        if (hasToken) {
          workerRef.current.postMessage({ action: 'start' });
        }
      } catch (error) {
        console.error('Ошибка при создании воркера:', error);
        return cleanup();
      }
    }
    return cleanup();
  }, []);
};
